import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  updateFastTrackName,
  shareFastTrackWithClientGroup,
  shareFastTrackWithUsers,
  readFastTrackType,
} from '@api/fast_tracks';
import { fastTrackStatus, flowTopicTag, setEndpoint } from '@helpers';
import {
  DownloadIcon,
  HelpIcon,
  OverlayTriggerTooltip,
  RepromptIcon,
  ShareIcon,
  TitleInput,
  toast,
  Toolbar,
} from '@utilities';

import ModalShare from '../../../ModalShare';
import Download from '../../../Builder/components/Toolbar/components/Download';
import TitleSelectedPrompts from './components/TitleSelectedPrompts';
import TitleFooter from './components/TitleFooter';
import { filterPrompts } from '../../../../pages/FastTracks/components/ModalBuildFastTrack/utilities/helpers';
import { getHelpArticleURLByFastTrackId, getIconByFastTrackId } from '../../utilities/helpers';
import styles from './_index.module.scss';

const FastTrackTitle = ({
  downloadData,
  flowData,
  handleReprompt,
  setFlow,
  showToolbar = true,
}) => {
  const [name, setName] = useState(null);
  const [newName, setNewName] = useState(null);
  const [showModalShare, setShowModalShare] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [isDownloadDropdownActive, setIsDownloadDropdownActive] = useState(false);

  const downloadRef = useRef(null);

  const downloadFileUrl = setEndpoint(`api/flows/${flowData?.id}/export`);

  const icon = getIconByFastTrackId(flowData?.fast_track_id);

  const hasPreviewTag = (data) => data?.tags && flowTopicTag.PREVIEW in data.tags;

  const downloadableData = useMemo(() => {
    if (!flowData || !downloadData || downloadData.size === 0) return null;

    // Sort data by topic order from flowData and filter out preview topics
    const orderedData = flowData.topics
      .filter((topic) => !hasPreviewTag(topic))
      .map((topic) => downloadData.get(topic.id))
      .filter(Boolean);

    return {
      data: orderedData,
      exportName: flowData.name || 'Narrative',
      // ModelBuilder assign date range to an object instead of an array when reprompt is selected
      reportDateRange: flowData.answers?.date_range?.date_range ||
        flowData?.answers?.date_range || [null, null],
      reportName: flowData.name || 'Narrative',
      reportType: `${flowData.fastTrackName} Narrative`,
      reportUrl: `${window.location.origin}/dashboard/narrative/${flowData.id}`,
    };
  }, [
    downloadData,
    flowData?.topics,
    flowData?.name,
    flowData?.answers?.date_range,
    flowData?.fast_track_name,
    flowData?.id,
  ]);

  const isDownloadAvailable = flowData?.status === fastTrackStatus.COMPLETED && downloadableData;
  const downloadTooltip = isDownloadAvailable
    ? 'Download'
    : 'Download will be available after report has finished processing';

  const shareTooltip =
    flowData?.status === fastTrackStatus.COMPLETED
      ? 'Share'
      : 'Share will be available after report has finished processing';

  useEffect(() => {
    if (!showToolbar || !flowData?.answers || !flowData?.id || !flowData?.fast_track_id) return;

    const initializeSelectedPrompts = async () => {
      try {
        const data = await readFastTrackType({
          fastTrackId: flowData?.fast_track_id,
          flowId: flowData?.id,
        });

        const prompts = data?.prompts || [];
        const selectedPrompts = filterPrompts(prompts);
        setSelectedPrompts(selectedPrompts);
      } catch (error) {
        console.error('Error fetching Narrative prompts:', error);
        toast('Failed to load prompts.', { status: 'error' });
      }
    };

    initializeSelectedPrompts();
  }, [flowData?.fast_track_id, flowData?.id, flowData?.answers, showToolbar]);

  useEffect(() => {
    if (!newName || !flowData?.id) return;

    const updateFlowName = async () => {
      try {
        await updateFastTrackName({ id: flowData.id, name: newName });
        setFlow((prev) => ({ ...prev, name: newName }));
      } catch (error) {
        console.error(error);
      }
    };

    updateFlowName();
  }, [newName]);

  return (
    <div>
      <div className={[styles['fast-track-title-toolbar']]}>
        {showToolbar ? (
          <Toolbar
            className={[styles['action-icons-container']]}
            tools={[
              <span ref={downloadRef}>
                <OverlayTriggerTooltip
                  aria-label="Download"
                  content={downloadTooltip}
                  key="download"
                >
                  <>
                    <button
                      className={styles['action-icon']}
                      data-testid="download-dropdown-button"
                      disabled={!isDownloadAvailable}
                      onClick={() => setIsDownloadDropdownActive(true)}
                    >
                      <DownloadIcon />
                    </button>
                    <Download
                      downloadData={downloadableData}
                      downloadFileUrl={downloadFileUrl}
                      downloadLabel={flowData?.fast_track_name}
                      isAvailable={isDownloadAvailable}
                      isDownloadDropdownActive={isDownloadDropdownActive}
                      setIsDownloadDropdownActive={setIsDownloadDropdownActive}
                      target={downloadRef?.current}
                    />
                  </>
                </OverlayTriggerTooltip>
              </span>,
              <OverlayTriggerTooltip aria-label="Share" content={shareTooltip} key="share">
                <button
                  className={[styles['action-icon']]}
                  data-testid="share-button"
                  disabled={flowData?.status !== fastTrackStatus.COMPLETED}
                  onClick={() => setShowModalShare(true)}
                >
                  <ShareIcon />
                </button>
              </OverlayTriggerTooltip>,
              <OverlayTriggerTooltip aria-label="Reprompt" content="Reprompt" key="reprompt">
                <button
                  className={[styles['action-icon']]}
                  data-testid="reprompt-button"
                  onClick={handleReprompt}
                >
                  <RepromptIcon />
                </button>
              </OverlayTriggerTooltip>,
              <OverlayTriggerTooltip aria-label="Help" content="Help">
                <a
                  className={[styles['action-icon']]}
                  data-log="Flow_Help_Center"
                  data-log-properties={JSON.stringify({
                    id: flowData?.id,
                    type: flowData?.fast_track_id,
                  })}
                  data-testid="help-button"
                  href={getHelpArticleURLByFastTrackId(flowData?.fast_track_id)}
                  rel="noreferrer"
                  target="_blank"
                >
                  <HelpIcon />
                </a>
              </OverlayTriggerTooltip>,
            ]}
          >
            <TitleInput
              autofocus={false}
              icon={icon}
              isDisabled={flowData?.isReadOnly}
              onBlur={setNewName}
              onChange={setName}
              value={name !== null ? name : flowData?.name}
            />
          </Toolbar>
        ) : (
          <div className={styles['draft-title-input']}>
            <TitleInput
              autofocus={false}
              icon={icon}
              isDisabled={true}
              onBlur={() => {}}
              onChange={() => {}}
              value={flowData?.name}
            />
          </div>
        )}
        {showToolbar && showModalShare && (
          <ModalShare
            hasSharePublicly={false}
            ids={[flowData?.id]}
            isSurvey={false}
            setShowModalShare={setShowModalShare}
            shareItemWithClients={shareFastTrackWithClientGroup}
            shareItemWithUsers={shareFastTrackWithUsers}
          />
        )}
        {showToolbar && (
          <TitleSelectedPrompts answers={flowData?.answers} selectedPrompts={selectedPrompts} />
        )}
        <TitleFooter
          answers={flowData?.answers}
          createdDatetime={flowData?.createdDatetime}
          fastTrackName={flowData?.fastTrackName}
        />
      </div>
    </div>
  );
};

FastTrackTitle.propTypes = {
  downloadData: PropTypes.object,
  flowData: PropTypes.object,
  handleReprompt: PropTypes.func,
  setFlow: PropTypes.func,
  showToolbar: PropTypes.bool,
};

export default FastTrackTitle;
